import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { CheckCircle2, AlertTriangle, X } from "lucide-react";
import { StoreHeader } from "../components/StoreHeader";
import { SRNAcknowledgement } from "../components/SRNAcknowledgement";
import { Input, Textarea, SubmitButton } from "../components/RegisterLayout";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { GROUP } from "../lib/site";
import { OrderMilestoneTracker } from "../components/OrderMilestoneTracker";

const MATERIALS = ["TMT Steel Bars", "Cement", "Structural Steel", "Binding Wire", "Plates & Sheets"];
const LEGACY_CATS = ["tmt", "cement", "structural", "binding_wire", "sheets"];
const DIAMETERS = [8, 10, 12, 16, 20, 25, 32];
const TMT_GRADES = ["Fe 500", "Fe 500D", "Fe 550D"];
const CEMENT_GRADES = ["Grade 43", "Grade 53", "PPC"];

const Select = ({ label, testid, value, onChange, children, required }) => (
  <label className="block group">
    <span className="overline text-[#8A8A82] group-focus-within:text-[#B87333] transition-colors">{label}{required && " *"}</span>
    <select
      data-testid={testid}
      value={value}
      onChange={onChange}
      className="w-full bg-transparent border-b border-white/15 focus:border-[#B87333] outline-none py-3 mt-2 font-body text-lg text-[#F5F5F0] transition-colors [&>option]:bg-[#0e0e0e] [&>option]:text-[#F5F5F0]"
    >
      {children}
    </select>
  </label>
);

const EMPTY = {
  name: "", email: "", phone: "", company: "",
  material: "TMT Steel Bars", grade: "", diameter: "",
  quantity: "", unit: "MT", length_per_bar: "12", num_bars: "",
  delivery_state: "", notes: "",
};

export default function RequestQuote() {
  const { user, api } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [missingFields, setMissingFields] = useState([]);   // pre-flight validator
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [catalogue, setCatalogue] = useState([]);           // [{phase, label, items:[name]}]

  useEffect(() => {
    Promise.all([api.get("/phases"), api.get("/products")]).then(([ph, pr]) => {
      const groups = ph.data.map((p) => ({
        phase: p.phase, label: `Phase ${p.phase} — ${p.label}`,
        items: pr.data.filter((x) => x.phase === p.phase && !LEGACY_CATS.includes(x.category)).map((x) => x.name),
      })).filter((g) => g.items.length);
      setCatalogue(groups);
    }).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
        company: f.company || user.business_name || user.company || "",
      }));
    }
  }, [user]);

  // Prefill from ladder-buy deep-link (?material=&qty=&unit=&grade=&notes=).
  // For TMT the form renders a tonnage calculator, not a plain Quantity field,
  // so we back-fill num_bars from the target tonnage using sensible defaults
  // (⌀12 mm × 12 m bars → ~93.75 bars per MT) and pin the target in notes.
  useEffect(() => {
    const material = params.get("material");
    const qty = params.get("qty");
    const unit = params.get("unit");
    const grade = params.get("grade");
    const notes = params.get("notes");
    if (!material && !qty && !unit && !grade && !notes) return;
    setForm((f) => {
      const nextMaterial = material || f.material;
      const isTmtPrefill = nextMaterial === "TMT Steel Bars";
      const qtyNum = parseFloat(qty || "");
      const tmtBackfill = (isTmtPrefill && qtyNum > 0 && (unit || "").toLowerCase().includes("mt"))
        ? {
            diameter: f.diameter || "12",
            length_per_bar: f.length_per_bar || "12",
            num_bars: String(Math.round((qtyNum * 1000) / ((12 * 12 / 162) * 12))),
          }
        : {};
      return {
        ...f,
        material: nextMaterial,
        grade: grade || f.grade,
        unit: unit || f.unit,
        quantity: qty || f.quantity,
        notes: notes || f.notes,
        ...tmtBackfill,
      };
    });
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isTmt = form.material === "TMT Steel Bars";
  const isCement = form.material === "Cement";

  // Live TMT tonnage: (D^2 / 162) * length_per_bar(m) * number_of_bars => kg -> Metric Tons
  const d = parseFloat(form.diameter);
  const lenPerBar = parseFloat(form.length_per_bar);
  const numBars = parseFloat(form.num_bars);
  const weightTons = (isTmt && d > 0 && lenPerBar > 0 && numBars > 0)
    ? ((d * d / 162) * lenPerBar * numBars) / 1000
    : null;

  const submit = async (e) => {
    e.preventDefault();
    // ═══ Vinci Pre-Flight Validator ═════════════════════════════
    // Eight critical fields required before the trade desk can lock
    // a rate. TMT swaps Quantity/Unit for Bar-count/Length.
    const preflight = [
      { key: "name",           label: "Full name" },
      { key: "email",          label: "Email" },
      { key: "company",        label: "Company" },
      { key: "material",       label: "Material" },
      isTmt
        ? { key: "diameter",   label: "Diameter" }
        : { key: "grade",      label: "Grade / specification" },
      isTmt
        ? { key: "num_bars",   label: "Number of bars" }
        : { key: "quantity",   label: "Quantity" },
      isTmt
        ? { key: "length_per_bar", label: "Length per bar (m)" }
        : { key: "unit",       label: "Unit (MT / bags / kg)" },
      { key: "delivery_state", label: "Delivery state" },
    ];
    const misses = preflight.filter(({ key }) => {
      const v = form[key];
      if (v === undefined || v === null) return true;
      const s = String(v).trim();
      // For numeric fields, blank OR zero counts as missing.
      if (["num_bars", "length_per_bar", "quantity"].includes(key)) {
        const n = parseFloat(s);
        return !s || Number.isNaN(n) || n <= 0;
      }
      return s.length === 0;
    });
    if (misses.length > 0) {
      setMissingFields(misses.map(m => m.key));
      setPreflightOpen(true);
      return;
    }
    setMissingFields([]);
    setLoading(true);
    try {
      const payload = {
        name: form.name, email: form.email, phone: form.phone, company: form.company,
        material: form.material,
        grade: form.grade,
        diameter: isTmt ? form.diameter : "",
        quantity: isTmt ? String(form.num_bars) : form.quantity,
        unit: isTmt ? "bars" : form.unit,
        length_per_bar: isTmt ? String(form.length_per_bar) : "",
        num_bars: isTmt ? String(form.num_bars) : "",
        weight_tons: weightTons != null ? Number(weightTons.toFixed(3)) : null,
        delivery_state: form.delivery_state, notes: form.notes,
      };
      const { data } = await api.post("/quote-requests", payload);
      setDone(data);
      toast.success(`Quote request ${data.ref} submitted.`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit your request.");
    } finally {
      setLoading(false);
    }
  };

  // Live re-clear a field's error the moment the user starts fixing it.
  const clearMiss = (k) => setMissingFields((arr) => arr.filter(x => x !== k));
  const missClass = (k) => missingFields.includes(k)
    ? "vinci-preflight-error"
    : "";

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0]">
      <Toaster position="bottom-right" theme="dark" richColors />
      <StoreHeader />

      <section className="max-w-[1100px] mx-auto px-6 md:px-12 pt-16 pb-24">
        <span className="overline text-[#B87333]">/ Request a Quote</span>
        <h1 className="font-serif text-4xl md:text-6xl mt-4 tracking-[-0.02em]">Tell us what you need.</h1>
        <p className="font-body text-[#8A8A82] mt-4 max-w-xl leading-relaxed">
          Specify the material, grade or diameter, quantity and delivery location. Our trade desk reviews every request and reverts with tiered, GST-compliant pricing.
        </p>

        {done ? (
          <div className="mt-12" data-testid="quote-success">
            <SRNAcknowledgement
              srn={done.srn}
              title="Quote Request Acknowledged"
              subtitle={`Our trade desk will lock rates and revert with a formal Quotation.
Reference ${done.ref} has been filed to the Vinci Master Ledger.`}
              kind="Quote Request · RFQ"
              summary={[
                ["Reference", done.ref],
                ["Material", `${done.material}${done.grade ? ` · ${done.grade}` : ""}${done.diameter ? ` · ⌀ ${done.diameter} mm` : ""}`],
                ["Quantity", `${done.quantity} ${done.unit || ""}`.trim()],
                ...(done.weight_tons != null ? [["Total weight", `${done.weight_tons} Metric Tons`]] : []),
                ...(done.delivery_state ? [["Delivery", done.delivery_state]] : []),
                ["Filed on", new Date().toLocaleString()],
              ]}
              actions={
                <>
                  <button
                    data-testid="quote-another"
                    onClick={() => { setDone(null); setForm({ ...EMPTY, name: form.name, email: form.email, company: form.company }); }}
                    className="inline-flex items-center rounded-full bg-[#C9A961] px-6 py-2.5 font-grotesk text-[11px] uppercase tracking-[0.24em] text-black hover:bg-[#B99751] transition-colors"
                  >
                    File another request
                  </button>
                  <button
                    data-testid="quote-to-shop"
                    onClick={() => nav("/shop")}
                    className="inline-flex items-center rounded-full border border-white/25 px-6 py-2.5 font-grotesk text-[11px] uppercase tracking-[0.24em] text-[#F5F5F0] hover:border-[#C9A961] transition-colors"
                  >
                    Browse the store →
                  </button>
                </>
              }
            />
            {/* Vinci Order Express · 8-Milestone strip after successful RFQ */}
            <div className="mt-14 border border-[#D8B23E]/20 rounded-sm p-6 md:p-8 bg-[#0a0906]/60"
                  data-testid="order-express-panel">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <div className="overline text-[#B87333]">Vinci Order Express · Roadmap</div>
                  <h3 className="font-serif text-2xl mt-1">Your trade will progress through 8 milestones</h3>
                </div>
                <span className="inline-flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#2FB999] border border-[#2FB999]/40 px-3 py-1.5">
                  Milestone 01 · Complete
                </span>
              </div>
              <OrderMilestoneTracker currentStage={2} compact />
            </div>
          </div>
        ) : (
          <motion.form
            data-testid="request-quote-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 grid md:grid-cols-2 gap-8"
          >
            <div className={missClass("name")} data-testid="wrap-name">
              <Input label="Full name *" testid="rq-name" value={form.name} onChange={(e) => { set("name")(e); clearMiss("name"); }} />
            </div>
            <div className={missClass("email")} data-testid="wrap-email">
              <Input label="Email *" testid="rq-email" type="email" value={form.email} onChange={(e) => { set("email")(e); clearMiss("email"); }} />
            </div>
            <Input label="Phone" testid="rq-phone" value={form.phone} onChange={set("phone")} />
            <div className={missClass("company")} data-testid="wrap-company">
              <Input label="Company *" testid="rq-company" value={form.company} onChange={(e) => { set("company")(e); clearMiss("company"); }} />
            </div>

            <div className={missClass("material")} data-testid="wrap-material">
              <Select label="Material" testid="rq-material" value={form.material} onChange={(e) => { setForm((f) => ({ ...f, material: e.target.value, grade: "", diameter: "" })); clearMiss("material"); }}>
                <optgroup label="Core Materials">
                  {MATERIALS.map((m) => <option key={m} value={m}>{m}</option>)}
                </optgroup>
                {catalogue.map((g) => (
                  <optgroup key={g.phase} label={g.label} data-testid={`rq-material-phase-${g.phase}`}>
                    {g.items.map((m) => <option key={m} value={m}>{m}</option>)}
                  </optgroup>
                ))}
                {form.material && !MATERIALS.includes(form.material) && !catalogue.some((g) => g.items.includes(form.material)) && (
                  <option value={form.material}>{form.material}</option>
                )}
              </Select>
            </div>

            {isTmt ? (
              <div className={missClass("diameter")} data-testid="wrap-diameter">
                <Select label="Diameter (mm) *" testid="rq-diameter" value={form.diameter} onChange={(e) => { set("diameter")(e); clearMiss("diameter"); }}>
                  <option value="">Select diameter</option>
                  {DIAMETERS.map((d) => <option key={d} value={`${d}`}>{d} mm</option>)}
                </Select>
              </div>
            ) : isCement ? (
              <div className={missClass("grade")} data-testid="wrap-grade">
                <Select label="Grade *" testid="rq-grade" value={form.grade} onChange={(e) => { set("grade")(e); clearMiss("grade"); }}>
                  <option value="">Select grade</option>
                  {CEMENT_GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                </Select>
              </div>
            ) : (
              <div className={missClass("grade")} data-testid="wrap-grade">
                <Input label="Grade / specification *" testid="rq-grade" value={form.grade} onChange={(e) => { set("grade")(e); clearMiss("grade"); }} />
              </div>
            )}

            {isTmt && (
              <Select label="Grade" testid="rq-tmt-grade" value={form.grade} onChange={set("grade")}>
                <option value="">Any</option>
                {TMT_GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
              </Select>
            )}

            {isTmt ? (
              <>
                <div className={missClass("length_per_bar")} data-testid="wrap-length">
                  <Input label="Length per bar (m) *" testid="rq-length" type="number" min="0" step="0.5" placeholder="e.g. 12" value={form.length_per_bar} onChange={(e) => { set("length_per_bar")(e); clearMiss("length_per_bar"); }} />
                </div>
                <div className={missClass("num_bars")} data-testid="wrap-num-bars">
                  <Input label="Number of bars *" testid="rq-num-bars" type="number" min="0" step="1" placeholder="e.g. 500" value={form.num_bars} onChange={(e) => { set("num_bars")(e); clearMiss("num_bars"); }} />
                </div>
                <div data-testid="tmt-tonnage" className="md:col-span-2 border border-[#B87333]/30 bg-[#B87333]/5 rounded-sm p-6 flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <span className="overline text-[#B87333] block">Tonnage calculator</span>
                    <span className="font-body text-xs text-[#8A8A82]">(⌀² ÷ 162) × length per bar × number of bars</span>
                  </div>
                  {weightTons != null ? (
                    <div className="text-right">
                      <span className="overline text-[#8A8A82] block mb-1">Total Weight</span>
                      <span data-testid="tmt-weight-value" className="font-serif text-3xl md:text-4xl text-[#F5F5F0]">
                        {weightTons.toFixed(3)} <span className="text-lg text-[#B87333]">Metric Tons</span>
                      </span>
                    </div>
                  ) : (
                    <span data-testid="tmt-weight-hint" className="font-body text-sm text-[#8A8A82]">
                      {form.diameter ? "Enter bar length and number of bars" : "Select a diameter to calculate tonnage"}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className={missClass("quantity")} data-testid="wrap-quantity">
                  <Input label="Quantity *" testid="rq-quantity" placeholder="e.g. 25" value={form.quantity} onChange={(e) => { set("quantity")(e); clearMiss("quantity"); }} />
                </div>
                <div className={missClass("unit")} data-testid="wrap-unit">
                  <Input label="Unit *" testid="rq-unit" placeholder="e.g. MT / bags / kg" value={form.unit} onChange={(e) => { set("unit")(e); clearMiss("unit"); }} />
                </div>
              </>
            )}

            <div className={missClass("delivery_state")} data-testid="wrap-state">
              <Select label="Delivery state *" testid="rq-state" value={form.delivery_state} onChange={(e) => { set("delivery_state")(e); clearMiss("delivery_state"); }}>
                <option value="">Select state</option>
                {GROUP.states.map((s) => <option key={s} value={s}>{s}</option>)}
              </Select>
            </div>

            <Textarea label="Notes" testid="rq-notes" rows={3} value={form.notes} onChange={set("notes")} className="md:col-span-2" />

            <div className="md:col-span-2 mt-2">
              <SubmitButton testid="rq-submit" type="submit" disabled={loading}>
                {loading ? "Submitting…" : "Submit request"}
              </SubmitButton>
            </div>
          </motion.form>
        )}
      </section>

      {/* ═══ Vinci Pre-Flight Validator Modal ═══════════════════════ */}
      <AnimatePresence>
        {preflightOpen && (
          <PreflightModal
            missing={missingFields}
            onClose={() => setPreflightOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Pulsing amber outline injected once — used on wrap-* divs. */}
      <style>{`
        @keyframes vinciAmberPulse {
          0%, 100% { box-shadow: 0 0 0 1px rgba(255,80,80,0.6), 0 0 12px 0 rgba(255,80,80,0.35); }
          50%      { box-shadow: 0 0 0 2px rgba(255,80,80,0.9), 0 0 22px 4px rgba(255,80,80,0.55); }
        }
        .vinci-preflight-error {
          border-radius: 4px;
          padding: 8px 10px;
          margin: -8px -10px;
          animation: vinciAmberPulse 1.6s ease-in-out infinite;
          background: rgba(192,57,43,0.06);
        }
        @keyframes vinciAmberBadgePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(216,178,62,0.7); }
          50%      { box-shadow: 0 0 0 14px rgba(216,178,62,0); }
        }
        .vinci-badge-pulse { animation: vinciAmberBadgePulse 1.8s ease-out infinite; }
      `}</style>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────
 *  Executive Directive · Pre-Flight modal
 *  Pulsing amber ring, non-blocking close, listed missing fields.
 * ───────────────────────────────────────────────────────────── */
function PreflightModal({ missing, onClose }) {
  const LABELS = {
    name: "Full name", email: "Email", company: "Company",
    material: "Material", grade: "Grade / specification",
    diameter: "Diameter (mm)", num_bars: "Number of bars",
    length_per_bar: "Length per bar (m)",
    quantity: "Quantity", unit: "Unit",
    delivery_state: "Delivery state",
  };
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm grid place-items-center p-4"
      data-testid="preflight-modal"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-lg w-full bg-[#0F0C05] border-2 border-[#D8B23E] rounded-sm overflow-hidden"
        style={{ boxShadow: "0 0 60px rgba(216,178,62,0.35)" }}
      >
        <div className="h-1 bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333]" />
        <button data-testid="preflight-close"
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 grid place-items-center border border-[#D8B23E]/40 hover:border-[#D8B23E] rounded-full transition-colors">
          <X className="w-3.5 h-3.5 text-[#D8B23E]" />
        </button>
        <div className="p-8">
          <div className="inline-flex items-center gap-3">
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#D8B23E]/15 border border-[#D8B23E] vinci-badge-pulse">
              <AlertTriangle className="w-5 h-5 text-[#D8B23E]" />
            </span>
            <div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.4em] text-[#8A6A1A]">
                Vinci Pre-Flight Validator
              </div>
              <h3 className="font-serif text-xl text-[#F5F5F0] mt-0.5">
                RF-Q STATUS: <span className="text-[#D8B23E]">INCOMPLETE DATA SET</span>
              </h3>
            </div>
          </div>
          <p className="mt-6 text-[#F6E4A6] font-body text-sm leading-relaxed italic border-l-2 border-[#D8B23E] pl-4">
            "Our synchronized supply chain requires 100% data precision to
            lock your daily rate. Please complete the highlighted fields
            below — every entry directly binds the trade desk's response."
          </p>
          <div className="mt-6">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A6A1A] mb-3">
              Required &middot; {missing.length} field{missing.length === 1 ? "" : "s"}
            </div>
            <ul className="space-y-2" data-testid="preflight-missing-list">
              {missing.map((k) => (
                <li key={k}
                    data-testid={`preflight-miss-${k}`}
                    className="flex items-center gap-3 text-sm text-[#F5F5F0]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C0392B] animate-pulse" />
                  {LABELS[k] || k}
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-8 flex items-center justify-end gap-3">
            <button
              data-testid="preflight-fix-now"
              onClick={onClose}
              className="px-6 py-3 bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk text-[11px] uppercase tracking-[0.32em] hover:shadow-[0_0_30px_rgba(216,178,62,0.5)] transition-shadow"
            >
              Fix Now &rarr;
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
// ─────────────────────────────────────────────────────────────
